import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionTimeout, setSessionTimeout] = useState(null);
    const SESSION_DURATION = 3600000; // 1 hour in milliseconds

    const signup = async (email, password) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            startSessionTimer();
            return result;
        } catch (error) {
            handleAuthError(error);
            throw error;
        }
    };

    const login = async (email, password) => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            startSessionTimer();
            return result;
        } catch (error) {
            handleAuthError(error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            clearSessionTimer();
        } catch (error) {
            handleAuthError(error);
            throw error;
        }
    };

    const signInWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            startSessionTimer();
            return result;
        } catch (error) {
            handleAuthError(error);
            throw error;
        }
    };

    const handleAuthError = (error) => {
        let errorMessage = 'An error occurred during authentication.';
        switch (error.code) {
            case 'auth/user-not-found':
                errorMessage = 'No account found with this email.';
                break;
            case 'auth/wrong-password':
                errorMessage = 'Incorrect password.';
                break;
            case 'auth/too-many-requests':
                errorMessage = 'Too many login attempts. Please try again later.';
                break;
            case 'auth/email-already-in-use':
                errorMessage = 'This email is already registered.';
                break;
            case 'auth/weak-password':
                errorMessage = 'Password should be at least 6 characters.';
                break;
            case 'auth/network-request-failed':
                errorMessage = 'Network error. Please check your connection.';
                break;
            case 'auth/invalid-email':
                errorMessage = 'Invalid email address.';
                break;
            case 'auth/user-disabled':
                errorMessage = 'This account has been disabled.';
                break;
            default:
                errorMessage = error.message;
        }
        throw new Error(errorMessage);
    };

    const startSessionTimer = () => {
        clearSessionTimer();
        const timeoutId = setTimeout(() => {
            logout();
            window.alert('Your session has expired. Please login again.');
        }, SESSION_DURATION);
        setSessionTimeout(timeoutId);
    };

    const clearSessionTimer = () => {
        if (sessionTimeout) {
            clearTimeout(sessionTimeout);
            setSessionTimeout(null);
        }
    };

    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            handleAuthError(error);
            throw error;
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
            if (user) {
                startSessionTimer();
            } else {
                clearSessionTimer();
            }
        });

        return () => {
            unsubscribe();
            clearSessionTimer();
        };
    }, []);

    const value = {
        currentUser,
        signup,
        login,
        logout,
        signInWithGoogle,
        resetPassword
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
